/**
 * מועדף = **קישור למאכל + כמות**, לא צילום רגע (בר, 30.09.2026: "זה לא אמור להיות צילום של אותו הרגע,
 * אלא קישור לקפה. אם עדכנתי את הקלוריות של מאכל, זה כי דייקתי אותו").
 *
 * בטעינה, כל פריט במועדף שמקושר למאכל — `customFoodId` (מאכל אישי) או `foodCode` (המאגר הלאומי) — מקבל
 * את השם והערכים העדכניים שלו, מוכפלים בכמות השמורה (grams). מועדף ישן בלי קישור, ששמו זהה בדיוק
 * למאכל אישי של אותה משתמשת — מקושר לפי השם. פריט בלי מקור (למשל מ-AI) נשאר כפי שנשמר.
 * **היומן לא מושפע** — מה שנרשם נשאר כפי שנרשם, כדי שימים קודמים לא ישנו את הסכום שלהם בדיעבד.
 *
 * טהור (בלי רשת) — נבדק ב-favLink.test.ts.
 */
import type { EntryItem } from '../services/entries';

export interface LinkSource {
  name_he: string;
  /** ל-100 ג' */
  calories: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  portions: Array<{ name_he: string; grams: number }>;
  icon?: string | null;
}

export interface FavShape {
  items: EntryItem[];
  calories: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
}

const round1 = (n: number): number => Math.round(n * 10) / 10;

/** פריט מועדף ← המאכל שהוא מקושר אליו, בכמות השמורה. בלי משקל אי אפשר להכפיל — נשאר כפי שנשמר. */
export function linkItem(it: EntryItem, src: LinkSource): EntryItem {
  const g = it.grams && it.grams > 0 ? it.grams : 0;
  if (!g) return it;
  const f = g / 100;
  const out: EntryItem = {
    ...it,
    name_he: src.name_he,
    calories: Math.round(src.calories * f),
    protein_g: round1((src.protein_g ?? 0) * f),
    carbs_g: round1((src.carbs_g ?? 0) * f),
    fat_g: round1((src.fat_g ?? 0) * f),
  };
  if (src.icon) out.icon = src.icon;
  // היחידה: זו שהוגדרה במאכל (אם שמה זהה — המשקל העדכני שלה), ואם לפריט אין — המנה הראשונה של המאכל
  const portions = (src.portions ?? []).filter((p) => p && p.name_he && p.grams > 0);
  const same = it.unit_name ? portions.find((p) => p.name_he === it.unit_name) : undefined;
  if (same) out.unit_grams = same.grams;
  else if (!it.unit_name && portions[0]) { out.unit_name = portions[0].name_he; out.unit_grams = portions[0].grams; }
  return out;
}

/**
 * כל המועדפים ← המאכלים שלהם. `customs` — המאכלים האישיים של המשתמשת (לפי id), `national` — מהמאגר (לפי קוד).
 * הסכומים של המועדף מחושבים מחדש מהפריטים (השורה בלשונית "מועדפים" מציגה אותם).
 */
export function resolveFavorites<T extends FavShape>(
  favs: T[],
  customs: Map<string, LinkSource & { id: string }>,
  national: Map<number, LinkSource>,
): T[] {
  const byName = new Map<string, LinkSource & { id: string }>();
  for (const c of customs.values()) if (!byName.has(c.name_he)) byName.set(c.name_he, c);
  return favs.map((fav) => {
    let changed = false;
    const items = (fav.items ?? []).map((it) => {
      let src: LinkSource | undefined;
      let extra: Partial<EntryItem> = {};
      if (it.customFoodId) src = customs.get(it.customFoodId);
      else if (it.foodCode !== undefined && it.foodCode !== null) src = national.get(it.foodCode);
      else {
        const c = byName.get(it.name_he);
        if (c) { src = c; extra = { customFoodId: c.id }; }
      }
      if (!src) return it;
      changed = true;
      return { ...linkItem(it, src), ...extra };
    });
    if (!changed) return fav;
    const sum = (k: 'calories' | 'protein_g' | 'carbs_g' | 'fat_g') => items.reduce((n, i) => n + (i[k] ?? 0), 0);
    return {
      ...fav, items,
      calories: Math.round(sum('calories')),
      protein_g: round1(sum('protein_g')), carbs_g: round1(sum('carbs_g')), fat_g: round1(sum('fat_g')),
    };
  });
}
